import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import moment from 'moment';

export const REFERRAL_REASONS = [
  { label: 'Personal history', value: 'personal_history' },
  { label: 'Family history', value: 'family_history' },
  { label: 'Suspected diagnosis', value: 'suspected_diagnosis' },
  { label: 'Carrier testing', value: 'carrier_testing' },
  { label: 'Predictive testing', value: 'predictive_testing' },
  { label: 'Advanced maternal age', value: 'advanced_maternal_age' }
];

export const REFERRAL_TRIAGE = [
  { label: 'Immediate', value: 'immediate' },
  { label: 'Urgent', value: 'urgent' },
  { label: 'Semi-urgent', value: 'semi_urgent' },
  { label: 'Standard', value: 'standard' }
];

export const CONTACT_TYPES = [
  { label: 'Contact with patient', value: 'contact_with_patient' },
  {
    label: 'Contact with patient’s doctor',
    value: 'contact_with_patient_doctor'
  },
  { label: 'Post clinic follow-up', value: 'post_clinic_follow_up' },
  { label: 'Pre-clinic request', value: 'pre_clinic_request' },
  { label: 'Case review', value: 'case_review' },
  { label: 'Risk notification', value: 'risk_notification' }
];

export const SAMPLE_TYPES = [
  { label: 'Blood', value: 'blood' },
  { label: 'Buccal swab', value: 'buccal_swab' },
  { label: 'Saliva', value: 'saliva' },
  { label: 'Hair', value: 'hair' },
  { label: 'Prepared DNA extract', value: 'prepared_dna_extract' }
];

export const CONSENT_GIVEN_BY = [
  { label: 'Self', value: 'self' },
  { label: 'Parent', value: 'parent' },
  { label: 'Child', value: 'child' },
  { label: 'Other relative', value: 'other_relative' },
  { label: 'Guardian', value: 'guardian' },
  { label: 'Power of Attorney', value: 'power_of_attorney' }
];

export const CONSENT_RESPONSE = [
  { label: 'Consent given', value: 'consent_given' },
  { label: 'Denied consent', value: 'denied_consent' },
  { label: 'Partial consent', value: 'partial_consent' },
  { label: 'Withdrawn consent', value: 'withdrawn_consent' },
  { label: 'No response', value: 'no_response' }
];

export const VARIANT_INTERPRETATION_OPTIONS = [
  { label: 'Pathogenic', value: 'Pathogenic' },
  { label: 'Likely Pathogenic', value: 'Likely Pathogenic' },
  {
    label: 'Variant of Uncertain Significance',
    value: 'Variant of Uncertain Significance'
  },
  { label: 'Likely Benign', value: 'Likely Benign' },
  { label: 'Benign', value: 'Benign' }
];

// Whether a variant was inherited or acquired.
//
// This is not a cosmetic detail: a somatic variant cannot be transmitted, so
// it is not evidence of a Mendelian cause. With no way to record it, the
// pedigree analyzer had to treat every pathogenic variant in an affected
// person as heritable, which is how a somatic JAK2 driver on a myelofibrosis
// patient was reported as autosomal dominant vertical transmission.
//
// "Not stated" is the default and is deliberately NOT the same as germline:
// the analyzer treats it as potentially heritable (matching how records
// created before this field existed already behave), so somatic has to be
// stated positively to change anything.
export const VARIANT_ORIGIN_OPTIONS = [
  { label: 'Not stated', value: 'unknown' },
  { label: 'Germline (inherited)', value: 'germline' },
  { label: 'Somatic (acquired)', value: 'somatic' }
];

// Seed defaults — used as fallback when the DB has no value for a typeKey yet.
// Add new type keys here as features grow.
const CLINICAL_DEFAULTS_MAP = {
  referralTypes: [
    { label: 'Self' },
    { label: 'GP' },
    { label: 'Hospital' },
    { label: 'Private Provider' }
  ],
  appointmentTypes: [
    { label: 'Self' },
    { label: 'GP' },
    { label: 'Hospital' },
    { label: 'Private Provider' }
  ],
  testNames: [],
  testTypes: [],
  // Also backs Tissue Pathology's "Path Lab" picker, which had no options at
  // all (TGCLOUD-1788). A laboratory is a laboratory — a site that has typed
  // its labs in once on a genetic test should not retype them on a pathology
  // record.
  laboratories: [],
  testResults: [],
  serviceProviders: [],
  processTypes: [],
  processStatuses: [],
  // TGCLOUD-1788 — these four pickers each held a hardcoded or permanently
  // empty array in the component, with no setter and no fetch, so a value typed
  // into them was accepted for the record being written and then vanished. They
  // are seeded with exactly the values the components used to hardcode, so no
  // existing option disappears; `consentTypes` starts empty because there was
  // never a list to preserve and inventing a consent vocabulary is not ours to
  // do.
  consentTypes: [],
  contactModes: [
    { label: 'Letter' },
    { label: 'Email' },
    { label: 'Telephone' },
    { label: 'Video Call' }
  ],
  appointmentLocations: [
    { label: 'Telephone' },
    { label: 'Video' },
    { label: 'Face to Face' },
    { label: 'Private Provider' }
  ]
};

const _fetchClinicalNotes = async () => {
  const res = await axios.get(APICONSTANTS.getPreferences);
  return res.data?.clinicalNotes ?? {};
};

const _saveClinicalNotes = async (clinicalNotes) => {
  await axios.put(APICONSTANTS.updatePreferences, { clinicalNotes });
};

export const getClinicalDefaults = async (typeKey) => {
  const clinicalNotes = await _fetchClinicalNotes();
  return clinicalNotes[typeKey] ?? CLINICAL_DEFAULTS_MAP[typeKey] ?? [];
};

// Compare two option labels for "is this the same value?".
//
// Case- and whitespace-insensitive (TGCLOUD-1788). The exact `===` this replaces
// is how "Personal History" came to sit in a list next to an existing
// "Personal history": the duplicate check missed, a second option was appended,
// and the drop-down then offered a clinician two entries that mean the same
// thing with no way to tell them apart. For a field that is later grouped or
// counted, two spellings of one value is a reporting defect, not just untidy.
const sameLabel = (a, b) =>
  String(a ?? '')
    .trim()
    .toLowerCase() ===
  String(b ?? '')
    .trim()
    .toLowerCase();

export const saveClinicalDefault = async (typeKey, label) => {
  const clinicalNotes = await _fetchClinicalNotes();
  const current =
    clinicalNotes[typeKey] ?? CLINICAL_DEFAULTS_MAP[typeKey] ?? [];
  // First spelling wins: an existing option is never rewritten to match the
  // casing someone just typed, because the stored records already carry the
  // stored spelling.
  if (current.some((opt) => sameLabel(opt.label, label))) return current;
  const trimmed = String(label ?? '').trim();
  if (!trimmed) return current;
  const updated = [...current, { label: trimmed }];
  await _saveClinicalNotes({ ...clinicalNotes, [typeKey]: updated });
  return updated;
};

export const deleteClinicalDefault = async (typeKey, label) => {
  const clinicalNotes = await _fetchClinicalNotes();
  const current =
    clinicalNotes[typeKey] ?? CLINICAL_DEFAULTS_MAP[typeKey] ?? [];
  // Matched the same way as the add, so a list that already holds a case-variant
  // pair from before the guard above can still be cleaned up from the UI.
  const updated = current.filter((opt) => !sameLabel(opt.label, label));
  await _saveClinicalNotes({ ...clinicalNotes, [typeKey]: updated });
  return updated;
};

const getReferralTags = async (theme, memberId, familyId, response) => {
  if (!response)
    response = await axios.get(APICONSTANTS.referral, {
      params: { familyId }
    });

  let referralOptions = response.data;
  referralOptions = referralOptions.filter((referral) => {
    return referral.familyMemberId == memberId || referral.showInFamily;
  });
  let referralTags = [];
  for (const referral of referralOptions) {
    referralTags.push({
      _id: referral._id,
      label:
        referral.referredBy +
        ' ' +
        moment(referral.createdDate).format(theme.dateFormat)
    });
  }
  return referralTags;
};

const getConsentTags = async (theme, memberId, familyId, response) => {
  if (!response)
    response = await axios.get(APICONSTANTS.consent, {
      params: { familyId }
    });
  let options = response.data;
  options = options.filter((option) => {
    return option.familyMemberId == memberId;
  });
  let Tags = [];
  for (const option of options) {
    Tags.push({
      _id: option._id,
      label:
        option.consentType +
        ' ' +
        moment(option.createdDate).format(theme.dateFormat)
    });
  }
  return Tags;
};

const getDependencies = async (familyId, _id, dateFormat) => {
  let dependences = [];
  await axios
    .get(`/clinical-notes/all`, {
      params: { familyId }
    })
    .then((res) => {
      for (const note of res.data) {
        if (
          note.associatedReferral?._id == _id ||
          note.associatedConsent?._id == _id
        ) {
          dependences.push(
            note.noteType +
              ' created ' +
              moment(note.createdAt).format(dateFormat)
          );
        }
      }
    });
  return dependences;
};

//Keeping these here just in case I need them, for now they need fixing which I have no reason to do
//const getAppointmentTags = async (response) => {
//  if (!response) response = await axios.get(`/api/${APICONSTANTS.appointment}`);
//  const options = response.data;
//  let Tags = [];
//  for (const option of options) {
//    //Consent has no name so I couldn't use that
//    Tags.push(option.clinicName + ' ' + option.appointmmentDate.split('T')[0]);
//  }
//  return Tags;
//};

//const getContactTags = async (response) => {
//  if (!response) response = await axios.get(`/api/${APICONSTANTS.contact}`);
//  const options = response.data;
//  let Tags = [];
//  for (const option of options) {
//    //Consent has no name so I couldn't use that
//    Tags.push(option.contactType + ' ' + option.contactDate.split('T')[0]);
//  }
//  return Tags;
//};
//
//
//const getSampleTags = async (response) => {
//  if (!response) response = await axios.get(`/api/${APICONSTANTS.sample}`);
//  const options = response.data;
//  let Tags = [];
//  for (const option of options) {
//    //Consent has no name so I couldn't use that
//    Tags.push(option.sampleType + ' ' + option.dateTaken.split('T')[0]);
//  }
//  return Tags;
//};
//
//const getTestTags = async (response) => {
//  if (!response) response = await axios.get(`/api/${APICONSTANTS.test}`);
//  const options = response.data;
//  let Tags = [];
//  for (const option of options) {
//    //Consent has no name so I couldn't use that
//    Tags.push(option.testName + ' ' + option.resultDueDate.split('T')[0]);
//  }
//  return Tags;
//};
//
//const getAddressTags = async (response) => {
//  if (!response) response = await axios.get(`/api/${APICONSTANTS.address}`);
//  const options = response.data;
//  let Tags = [];
//  for (const option of options) {
//    //Consent has no name so I couldn't use that
//    Tags.push(option.postCode + ' ' + option.town);
//  }
//  return Tags;
//};
//
//const getClinicalNoteTags = async (response) => {
//  if (!response)
//    response = await axios.get(`/api/${APICONSTANTS.clinicalNote}`);
//  const options = response.data;
//  let Tags = [];
//  for (const option of options) {
//    //Consent has no name so I couldn't use that
//    Tags.push(option.clinician + ' ' + option.date.split('T')[0]);
//  }
//  return Tags;
//};

const getAddressFlags = async ({ familyId, familyMemberId }) => {
  const res = await axios.get(
    APICONSTANTS.fetchAddressFlags
      .replace(':familyId', familyId)
      .replace(':familyMemberId', familyMemberId)
  );

  return res.data || {};
};

const handleUpload = async (e) => {
  if (e.target.files.length !== 0) {
    const formData = new FormData();
    formData.append('file_attachment', e.target.files[0]);
    axios.post(APICONSTANTS.fileAttachment, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }
};

export const getRecordDate = (record) => {
  switch (record.noteType) {
    case 'Appointment':
      return record.appointmentDate;
    case 'Referral':
      return record.referralDate;
    case 'Test':
      return record.dateRequested || record.dateResultReceived;
    case 'Sample':
      return record.dateTaken;
    case 'Clinical Note':
      return record.clinicalNoteDate;
    case 'Contact':
      return record.contactDate;
    case 'Consent':
      return record.consentDate || record.consentRequestDate;
    case 'Treatment':
      return record.treatmentDate;
    case 'Tissue Pathology':
      return record.pathReportDate;
    case 'Process':
      return record.processStartDate;
    case 'Disease':
      return record.dateReported;
    case 'Phenotype':
    case 'Genotype':
      return record.dateRecorded;
    case 'Risk Score':
      return record.createdAt;
    case 'Pregnancy':
      return record.createdAt;
    default:
      return record.createdAt;
  }
};

export const getRecordTitle = (record) => {
  switch (record.noteType) {
    case 'Appointment':
      return record.clinicName || record.appointmentType || 'Appointment';
    case 'Referral':
      return (
        record.referralReason ||
        record.referralType ||
        (record.referredBy ? `Referral by ${record.referredBy}` : 'Referral')
      );
    case 'Test':
      return record.testName || record.testType || 'Test';
    case 'Sample':
      return record.sampleType || record.trackingId || 'Sample';
    case 'Clinical Note':
      return (
        (record.note || '').split('\n')[0].slice(0, 100) || 'Clinical Note'
      );
    case 'Contact':
      return record.reasonForContact || record.contactType || 'Contact';
    case 'Consent':
      return record.consentType || 'Consent';
    case 'Treatment':
      return record.treatment || 'Treatment';
    case 'Pregnancy':
      return 'Pregnancy';
    case 'Address':
      return (
        [record.addressLine1, record.town || record.city]
          .filter(Boolean)
          .join(', ') || 'Address'
      );
    case 'Tissue Pathology':
      return (
        [record.tissueSite, record.morphology].filter(Boolean).join(' — ') ||
        'Tissue Pathology'
      );
    case 'Process':
      return record.processType || 'Process';
    case 'Disease':
      return record.name || 'Disease';
    case 'Phenotype':
      return record.name || 'Phenotype';
    case 'Genotype':
      return record.name || 'Genotype';
    case 'Risk Score':
      return record.riskLabel || 'Risk Score';
    default:
      return record.noteType || 'Record';
  }
};

export const getSearchableText = (record) =>
  [
    getRecordTitle(record),
    record.noteType,
    record.note,
    record.referralReason,
    record.referralNotes,
    record.appointmentNotes,
    record.testNotes,
    record.testResult,
    record.testName,
    record.consentNotes,
    record.contactNotes,
    record.comment,
    record.reasonForContact,
    record.sampleType,
    record.sampleStatus,
    record.treatment,
    record.referralType,
    record.referralTo,
    record.referredBy,
    record.clinicName,
    record.clinician,
    record.testType,
    record.addressLine1,
    record.addressLine2,
    record.town,
    record.city,
    record.postCode,
    record.country,
    record.laboratory,
    record.tissueSite,
    record.morphology,
    record.consentType,
    record.contactType,
    record.id,
    record.carrierStatus,
    record.riskLabel,
    record.gailRiskScore != null ? String(record.gailRiskScore) : null,
    record.prsResult?.trait,
    record.prsResult?.pgsId
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

export {
  getAddressFlags,
  getConsentTags,
  getReferralTags,
  getDependencies,
  //getAddressTags,
  //getClinicalNoteTags,
  //getTestTags,
  //getSampleTags,
  //getContactTags,
  //getAppointmentTags,
  handleUpload
};
